import { Button } from "@package/subframe/components/Button";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherInfo, FeatherKeyRound } from "@subframe/core";
import { useState } from "react";

import {
  completePinSetup,
  interpretPinLoginError,
  invalidPinMessage,
  isNetworkError,
  sanitizePin,
  type SignInWithPinResponse,
} from "./pin-login-api";

const PIN_LENGTH = 4;
const MISMATCH_MESSAGE = "PINs do not match. Please try again.";
const NETWORK_MESSAGE =
  "Could not save PIN. Check your connection and try again.";
const LOCKED_MESSAGE = "Account locked. Ask your manager to reset your PIN.";
const RESET_MESSAGE = "Your manager has reset your PIN.";
const GENERIC_ERROR = "Something went wrong. Please try again.";

interface FirstTimePinSetupProps {
  /** Credential proving this device was linked by a manager. */
  deviceSecret: string;
  userId: string;
  /** The name of the user who is setting their PIN. */
  displayName: string;
  /** The temporary PIN the user just verified on the login screen. */
  temporaryPin: string;
  /** Show the "manager has reset your PIN" explanation. */
  wasResetByManager: boolean;
  /** Return to the user list. */
  onBack: () => void;
  /** Called with the sign-in response once the new PIN is saved. */
  onSuccess: (response: SignInWithPinResponse) => Promise<void>;
}

/**
 * First-time PIN setup. Reached from PinLoginShell after the user
 * enters a correct temporary PIN: they choose their own 4-digit PIN, confirm
 * it, and are logged straight in. This step cannot be skipped — there is no
 * path to a session while a temporary PIN is pending.
 */
export function FirstTimePinSetup({
  deviceSecret,
  userId,
  displayName,
  temporaryPin,
  wasResetByManager,
  onBack,
  onSuccess,
}: FirstTimePinSetupProps) {
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function onFieldChange(setter: (value: string) => void) {
    return (value: string) => {
      setter(sanitizePin(value, PIN_LENGTH));
      setError(null);
    };
  }

  async function handleSubmit() {
    if (newPin.length !== PIN_LENGTH || confirmPin.length !== PIN_LENGTH) {
      return;
    }
    // A mismatch is a setup mistake, not a failed login — clear both fields
    // and let the user try again without touching the lockout counter.
    if (newPin !== confirmPin) {
      setNewPin("");
      setConfirmPin("");
      setError(MISMATCH_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await completePinSetup(
        deviceSecret,
        userId,
        temporaryPin,
        newPin,
      );
      await onSuccess(response);
    } catch (err) {
      setNewPin("");
      setConfirmPin("");
      if (isNetworkError(err)) {
        setError(NETWORK_MESSAGE);
      } else {
        const result = interpretPinLoginError(err);
        switch (result.kind) {
          case "locked":
            setError(LOCKED_MESSAGE);
            break;
          case "invalid":
            // The temporary PIN stopped matching (e.g. it was reset again) —
            // the user must go back and re-enter it.
            setError(invalidPinMessage(result.attemptsRemaining));
            break;
          default:
            setError(GENERIC_ERROR);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      className="flex w-full flex-col items-start gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <div className="flex w-full flex-col items-start gap-2">
        <FeatherKeyRound className="text-heading-1 text-brand-primary" />
        <span className="text-heading-2 font-heading-2 text-neutral-900">
          Choose your PIN
        </span>
        {wasResetByManager && (
          <div className="flex w-full items-center gap-2 rounded-md bg-secondary-beige-50 px-4 py-3">
            <FeatherInfo className="text-subtext-color" />
            <span className="text-body font-body text-default-font">
              {RESET_MESSAGE}
            </span>
          </div>
        )}
        <span className="text-body font-body text-subtext-color">
          {displayName}, pick a 4-digit PIN you&apos;ll use to log in on this
          device. Only you should know it.
        </span>
      </div>

      <TextField
        className="h-auto w-full flex-none"
        size="large"
        label="New PIN"
        error={!!error}
      >
        <TextField.Input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          placeholder="Enter a 4-digit PIN"
          value={newPin}
          disabled={isSubmitting}
          onChange={(e) => onFieldChange(setNewPin)(e.target.value)}
        />
      </TextField>

      <TextField
        className="h-auto w-full flex-none"
        size="large"
        label="Confirm PIN"
        error={!!error}
        helpText={error ?? undefined}
      >
        <TextField.Input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Re-enter your new PIN"
          value={confirmPin}
          disabled={isSubmitting}
          onChange={(e) => onFieldChange(setConfirmPin)(e.target.value)}
        />
      </TextField>

      <Button
        className="h-12 w-full flex-none"
        variant="brand-primary"
        size="large"
        type="submit"
        disabled={
          isSubmitting ||
          newPin.length !== PIN_LENGTH ||
          confirmPin.length !== PIN_LENGTH
        }
        loading={isSubmitting}
      >
        Save PIN and log in
      </Button>

      <LinkButton variant="neutral" size="small" onClick={onBack}>
        Choose a different user
      </LinkButton>
    </form>
  );
}
